import { type AnalyticsEvent } from '@autone/analytics';
import { useMemo, useState } from 'react';
import { type Control, type FieldValues, type Path } from 'react-hook-form';

import {
  type GetCoreProductManufacturersRequest,
  useGetCoreProductManufacturersQuery,
} from '../../../../redux/services';
import { TailwindFormField } from '../../../../tailwind';
import { MultiSelectDropdown } from '../../../multi-select-dropdown/MultiSelectDropdown';
import { MultiselectItemWithId } from '../../CoreApiMultiselectItems';
import { useMultiselectApiFilters } from '../../hooks';
import { type GetMultiselectMapper } from '../../types';
import {
  CLIENT_SIDE_SEARCH_DROPDOWN_LIMIT,
  getConcatenatedOptionId,
  getScopeItemFromConcatenatedId,
} from '../../utilities';
import { getRelevantCoreProductFilterKeys } from '../utilities/core-product-filter-keys';

type ManufacturerFilters =
  GetCoreProductManufacturersRequest['body']['filters'];
interface Props<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  catalogueMap?: GetMultiselectMapper<ManufacturerFilters['catalogue'], T>;
  productMap?: GetMultiselectMapper<ManufacturerFilters['product'], T>;
  productGroupsMap?: GetMultiselectMapper<
    ManufacturerFilters['product_groups'],
    T
  >;
  locationClustersMap?: GetMultiselectMapper<
    ManufacturerFilters['location_cluster'],
    T
  >;
  disabled?: boolean;
  analyticsEvent?: AnalyticsEvent;
}

export const CoreProductManufacturersMultiselect = <T extends FieldValues>({
  control,
  name,
  catalogueMap,
  productMap,
  productGroupsMap,
  locationClustersMap,
  disabled,
  analyticsEvent,
}: Props<T>) => {
  const catalogueFilters = useMultiselectApiFilters({
    control,
    map: catalogueMap,
    relevantFilterKeys: ['seasons', 'events'],
  });

  const productFilters = useMultiselectApiFilters({
    control,
    map: productMap,
    relevantFilterKeys: getRelevantCoreProductFilterKeys(['manufacturers']),
  });

  const locationClustersFilters = useMultiselectApiFilters({
    control,
    map: locationClustersMap,
    relevantFilterKeys: ['location_clusters'],
  });

  const productGroupsFilters = useMultiselectApiFilters({
    control,
    map: productGroupsMap,
    relevantFilterKeys: ['groups'],
  });

  const [searchTerm, setSearchTerm] = useState('');
  const { data, isLoading } = useGetCoreProductManufacturersQuery({
    query: {
      limit: CLIENT_SIDE_SEARCH_DROPDOWN_LIMIT,
    },
    body: {
      filters: {
        product: productFilters,
        catalogue: catalogueFilters,
        product_groups: productGroupsFilters,
        location_cluster: locationClustersFilters,
        size_run: {},
        size: {},
      },
    },
  });

  const options = useMemo(
    () =>
      data?.items.filter((option) =>
        option.description.toLowerCase().includes(searchTerm.toLowerCase()),
      ) ?? [],
    [data, searchTerm],
  );

  return (
    <TailwindFormField
      control={control}
      name={name}
      render={({ field: { value, onChange, disabled: fieldDisabled } }) => (
        <MultiSelectDropdown
          className="h-fit"
          loading={isLoading}
          options={options}
          getOptionId={getConcatenatedOptionId}
          getOptionLabel={(option) => option?.description}
          onSearchTermChange={setSearchTerm}
          selected={value?.map(getConcatenatedOptionId) ?? []}
          onSelectedChange={(ids) =>
            onChange(
              ids.length ? ids.map(getScopeItemFromConcatenatedId) : null,
            )
          }
          renderOption={(option) => <MultiselectItemWithId {...option} />}
          disabled={disabled || fieldDisabled}
          analyticsEvent={analyticsEvent}
        />
      )}
    />
  );
};
